/**
 * Telling a screen whose code could not be fetched from any other fault.
 *
 * A deploy replaces the hashed files an open tab still points at, so the next
 * screen that tab opens asks for a file that is gone. Browsers word the
 * rejection differently and a bundler or test runner may wrap it, so the
 * message is checked down the `cause` chain rather than on the outer error.
 */
const CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
]

export function isChunkLoadError(error: unknown): boolean {
  for (let at = error, depth = 0; at instanceof Error && depth < 5; depth++) {
    if (at.name === 'ChunkLoadError') return true
    if (CHUNK_MESSAGES.some((text) => at instanceof Error && at.message.includes(text))) {
      return true
    }
    at = at.cause
  }
  return false
}

const RELOADED_AT = 'sitefocus.cm.stale-build-reload'

/**
 * Reload once for a stale build, never in a loop.
 *
 * A reload fetches the new `index.html` and with it the files that exist. If
 * the page was reloaded for this reason within the last minute the newest
 * build is already loaded and another reload would not help, so this returns
 * false and the failure is left for the error boundary to explain.
 */
export function reloadForStaleBuild(now: number = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOADED_AT) ?? 0)
    if (now - last < 60_000) return false
    window.sessionStorage.setItem(RELOADED_AT, String(now))
  } catch {
    // No session storage (a private window, blocked site data): the boundary
    // still offers the reload by hand.
    return false
  }
  window.location.reload()
  return true
}
